import {
  ApplicationConfig,
  importProvidersFrom,
  APP_INITIALIZER,
} from '@angular/core';
import {
  TranslateModule,
  TranslateLoader,
  TranslateService,
} from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import {
  provideHttpClient,
  HttpClientModule,
  withInterceptors,
} from '@angular/common/http';
import { provideRouter } from '@angular/router';
import { routes } from './app.routes';

// No factory needed; using provideTranslateHttpLoader helper.
import { AuthInterceptor } from './shared/services/auth-interceptor.service';

// AOT-friendly factory for ngx-translate loader. Returns a simple TranslateLoader
// that loads `./assets/i18n/{lang}.json`.
export function HttpLoaderFactory(
  http: HttpClient,
  document: Document,
): TranslateLoader {
  // Build an absolute URL for the i18n assets using the document base href.
  // This avoids requests like '/current/route/assets/i18n/en.json' which
  // won't be served and won't appear under DevTools Sources.
  const prefix = new URL('assets/i18n/', document.baseURI).href;
  console.log('[i18n][HttpLoaderFactory] computed prefix =', prefix);
  return {
    getTranslation: (lang: string) =>
      http.get<Record<string, string>>(`${prefix}${lang}.json`),
  } as TranslateLoader;
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(
      withInterceptors([
        (req, next) => {
          // Do not attach auth header for local asset requests (i18n, images, etc.)
          // This covers both absolute '/assets' requests and relative 'assets/...'
          const url = req.url || '';
          if (
            url.startsWith('/assets') ||
            url.startsWith('assets') ||
            url.includes('/assets/')
          ) {
            console.log(
              '[i18n][interceptor] skipping auth header for asset request:',
              req.url,
            );
            return next(req);
          }

          const token = localStorage.getItem('auth_token');
          if (token) {
            const authReq = req.clone({
              setHeaders: { Authorization: `Bearer ${token}` },
            });

            return next(authReq);
          }
          return next(req);
        },
      ]),
    ),
    importProvidersFrom(
      HttpClientModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient, DOCUMENT],
        },
      }),
    ),
    provideRouter(routes),
    // Initialize language from localStorage if present
    {
      provide: APP_INITIALIZER,
      useFactory: (translate: TranslateService) => {
        return () => {
          const stored = localStorage.getItem('lang');
          const initial =
            stored && ['es', 'en'].includes(stored) ? stored : 'es';
          translate.setDefaultLang('es');
          return translate
            .use(initial)
            .toPromise()
            .catch(() => {
              console.warn(
                '[APP_INITIALIZER][i18n] failed to load language files for',
                initial,
              );
            });
        };
      },
      deps: [TranslateService],
      multi: true,
    },
  ],
};
